import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import TrainerLayout from './TrainerLayout';
import TemplatesManagement from './TemplatesManagement';
import { TrainerContext } from '../../context/TrainerContext';
import { FiFileText, FiAlertCircle, FiCalendar, FiClock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import '../../styles/TrainerReportsManagement.css';

const TrainerReportsManagement = () => {
    const { t, i18n } = useTranslation('reports');
    const { trainer } = useContext(TrainerContext);
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [pendingReports, setPendingReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('templates');
    const [pendingFilter, setPendingFilter] = useState('all');

    const fetchTemplates = async () => {
        if (!trainer?.schoolId) return;
        try {
            setLoading(true);
            const response = await axios.get(`/api/v1/reports/templates/school/${trainer.schoolId}`);
            setTemplates(response.data);
        } catch (err) {
            console.error('Error fetching templates:', err);
            setError(t('fetchTemplatesError'));
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingReports = async () => {
        if (!trainer?.schoolId) return;
        try {
            const response = await axios.get(`/api/v1/reports/pending/school/${trainer.schoolId}`);
            setPendingReports(response.data);
        } catch (err) {
            console.error('Error fetching pending reports:', err);
        }
    };

    useEffect(() => {
        fetchTemplates();
        fetchPendingReports();
    }, [trainer?.schoolId]);

    const handleTemplatesChange = (updatedTemplates) => {
        setTemplates(updatedTemplates);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const filteredPending = pendingReports.filter(r => {
        if (pendingFilter === 'overdue') return r.status === 'OVERDUE';
        if (pendingFilter === 'today') return r.status === 'DUE_TODAY';
        return true;
    });

    const overdueCount = pendingReports.filter(r => r.status === 'OVERDUE').length;
    const todayCount = pendingReports.filter(r => r.status === 'DUE_TODAY').length;

    if (!trainer?.schoolId) {
        return (
            <TrainerLayout>
                <div className="trm-container">
                    <h1 className="trm-mobile-title">{t('title')}</h1>
                    <p className="trm-error">{t('noSchoolData')}</p>
                </div>
            </TrainerLayout>
        );
    }

    return (
        <TrainerLayout>
            <div className="trm-container">
                <h1 className="trm-mobile-title">{t('title')}</h1>

                {/* Tab Navigation */}
                <div className="trm-tabs">
                    <button
                        className={`trm-tab ${activeTab === 'templates' ? 'trm-tab--active' : ''}`}
                        onClick={() => setActiveTab('templates')}
                    >
                        <FiFileText className="trm-tab-icon" />
                        {t('tabs.templates')}
                    </button>
                    <button
                        className={`trm-tab ${activeTab === 'pending' ? 'trm-tab--active' : ''}`}
                        onClick={() => { setActiveTab('pending'); fetchPendingReports(); }}
                    >
                        <FiAlertCircle className="trm-tab-icon" />
                        {t('tabs.pending')}
                        {pendingReports.length > 0 && (
                            <span className="trm-tab-badge">{pendingReports.length}</span>
                        )}
                    </button>
                </div>

                {activeTab === 'templates' && (
                    <>
                        {loading ? (
                            <div className="trm-loading"><p>{t('loadingTemplates')}</p></div>
                        ) : error ? (
                            <div className="trm-error-container">
                                <p className="trm-error">{error}</p>
                                <button className="trm-retry-btn" onClick={fetchTemplates}>{t('tryAgain')}</button>
                            </div>
                        ) : (
                            <TemplatesManagement
                                templates={templates}
                                schoolId={trainer.schoolId}
                                onTemplatesChange={handleTemplatesChange}
                            />
                        )}
                    </>
                )}

                {activeTab === 'pending' && (
                    <div className="trm-pending">
                        {/* Filter chips */}
                        <div className="trm-pending-filters">
                            <button
                                className={`trm-filter-chip ${pendingFilter === 'all' ? 'trm-filter-chip--active' : ''}`}
                                onClick={() => setPendingFilter('all')}
                            >
                                {t('pending.allPending')} ({pendingReports.length})
                            </button>
                            <button
                                className={`trm-filter-chip trm-filter-chip--overdue ${pendingFilter === 'overdue' ? 'trm-filter-chip--active' : ''}`}
                                onClick={() => setPendingFilter('overdue')}
                            >
                                {t('pending.overdue')} ({overdueCount})
                            </button>
                            <button
                                className={`trm-filter-chip trm-filter-chip--today ${pendingFilter === 'today' ? 'trm-filter-chip--active' : ''}`}
                                onClick={() => setPendingFilter('today')}
                            >
                                {t('pending.dueToday')} ({todayCount})
                            </button>
                        </div>

                        {filteredPending.length === 0 ? (
                            <div className="trm-pending-empty">
                                <FiFileText className="trm-pending-empty-icon" />
                                <p className="trm-pending-empty-title">{t('pending.empty')}</p>
                                <p className="trm-pending-empty-hint">{t('pending.emptyHint')}</p>
                            </div>
                        ) : (
                            <div className="trm-pending-list">
                                {filteredPending.map((report) => (
                                    <div
                                        key={report.submissionId}
                                        className={`trm-pending-item ${report.status === 'OVERDUE' ? 'trm-pending-item--overdue' : 'trm-pending-item--today'}`}
                                        onClick={() => navigate(`/dog/details/${report.dogId}`)}
                                    >
                                        <div className="trm-pending-item-main">
                                            <div className="trm-pending-item-info">
                                                <span className="trm-pending-item-dog">{report.dogName}</span>
                                                <span className="trm-pending-item-template">{report.templateTitle}</span>
                                            </div>
                                            <div className="trm-pending-item-meta">
                                                <span className={`trm-pending-status ${report.status === 'OVERDUE' ? 'trm-pending-status--overdue' : 'trm-pending-status--today'}`}>
                                                    {report.status === 'OVERDUE' ? (
                                                        <><FiClock size={12} /> {t('pending.overdue')}</>
                                                    ) : (
                                                        <><FiCalendar size={12} /> {t('pending.dueToday')}</>
                                                    )}
                                                </span>
                                                <span className="trm-pending-date">{formatDate(report.dueDate)}</span>
                                            </div>
                                        </div>
                                        {report.ownerName && (
                                            <span className="trm-pending-owner">{report.ownerName}</span>
                                        )}
                                        {!report.ownerName && (
                                            <span className="trm-pending-no-owner">{t('pending.noOwner')}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </TrainerLayout>
    );
};

export default TrainerReportsManagement;
